import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { CSVUploadModal } from './Master';

// IT Team Training.
//   · IT person → "My Training": their topics, the lesson, and a status they set.
//   · IT Lead   → the team dashboard.
// Leads see both, tabbed, because a lead is usually a trainee somewhere too.
const TRAINERS = ['admin', 'it_head', 'it_lead'];

const STATUS = {
  not_started: { label:'Not Started', colour:'var(--text2)', bg:'var(--bg3)',
                 hint:'I have not started this training yet.' },
  in_progress: { label:'In Progress', colour:'#B26A00',      bg:'rgba(255,153,0,0.15)',
                 hint:'I am currently going through the training materials.' },
  completed:   { label:'Completed',   colour:'var(--green)', bg:'rgba(45,168,102,0.15)',
                 hint:'I have read and understood the training materials.' },
};

const fmtDate  = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtStamp = d => d ? new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
// The table shows a one-line gist; the full text lives in the lesson panel.
const summarise = (s, n = 70) => {
  const flat = String(s || '').replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();
  return flat ? (flat.length > n ? flat.slice(0, n) + '…' : flat) : '—';
};
const bullets = s => String(s || '').split('\n').map(l => l.replace(/^\s*[-•*\d.)]+\s*/, '').trim()).filter(Boolean);

export default function Training({ openId, clearArg }) {
  const { user } = useAuth();
  const isLead = TRAINERS.includes(user?.role);
  const [view, setView] = useState(isLead ? 'team' : 'mine');
  const [openTraining, setOpenTraining] = useState(openId || null);
  // Set when planning from the Training Needs table: pre-fills the schedule
  // dialog with the topic and the people who asked for it.
  const [planFrom, setPlanFrom] = useState(null);

  useEffect(() => { if (openId) { setOpenTraining(openId); setView('mine'); } }, [openId]);

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <h2 style={{ fontSize:18, fontWeight:600 }}>🎓 IT Team Training</h2>
        {isLead ? (
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
            {[['team','Team Dashboard'],['needs','Training Needs'],['topics','Topics & Lessons'],
              ['mine','My Training'],['skills','My Skills']].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)}
                style={{ padding:'8px 18px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
                  borderBottom: view === id ? '2px solid var(--accent)' : '2px solid transparent',
                  color: view === id ? 'var(--accent)' : 'var(--text2)',
                  fontWeight: view === id ? 600 : 400, marginBottom:-1 }}>{label}</button>
            ))}
          </div>
        ) : (
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)' }}>
            {[['mine','My Training'],['skills','My Skills']].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)}
                style={{ padding:'8px 18px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
                  borderBottom: view === id ? '2px solid var(--accent)' : '2px solid transparent',
                  color: view === id ? 'var(--accent)' : 'var(--text2)',
                  fontWeight: view === id ? 600 : 400, marginBottom:-1 }}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {view === 'team'   && <TeamDashboard onOpen={id => { setOpenTraining(id); setView('mine'); }}
        planFrom={planFrom} clearPlan={() => setPlanFrom(null)} />}
      {view === 'needs'  && <TrainingNeeds onPlan={t => { setPlanFrom(t); setView('team'); }} />}
      {view === 'topics' && <TopicsTab />}
      {view === 'skills' && <MySkills />}
      {view === 'mine'   && <MyTraining openTraining={openTraining}
        setOpenTraining={id => { setOpenTraining(id); if (!id) clearArg?.(); }} />}
    </div>
  );
}

// ── My Skills: go through every topic and say what you need ─────────────────
function MySkills() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [notes, setNotes] = useState({});
  const [expanded, setExpanded] = useState({});   // topic_id -> sub-topics shown
  const load = useCallback(() => api.get('/trainings/assessment/mine').then(r => setD(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  // With a sub-topic id it answers that line; without, the whole topic.
  const answer = async (topic_id, state, subtopic_id = null) => {
    try {
      await api.post('/trainings/assessment', {
        topic_id, state, subtopic_id,
        note: subtopic_id ? null : (notes[topic_id] ?? null),
      });
      flash('✓ Saved'); load();
    } catch (e) { flash(e.response?.data?.error || 'Could not save'); }
  };

  const bulk = async (topic_id, state) => {
    try {
      const r = await api.post('/trainings/assessment/bulk', { topic_id, state });
      flash(`✓ Answered ${r.data.updated} sub-topic(s)`); load();
    } catch (e) { flash(e.response?.data?.error || 'Could not save'); }
  };

  if (!d) return null;
  const pct = d.total ? Math.round(d.answered / d.total * 100) : 0;

  return (
    <div>
      <div className="card" style={{ marginBottom:14 }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div>
            <b style={{ fontSize:14 }}>Your skills assessment</b>
            <div style={{ fontSize:12.5, color:'var(--text2)' }}>
              Go through every topic and tell us what you already know and what you want training on.
              Your answers decide what gets scheduled.
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:22, fontWeight:700, color: pct === 100 ? 'var(--green)' : 'var(--accent)' }}>
              {d.answered}/{d.total}
            </div>
            {/* Counted per question, not per topic — one topic can hold
                thirty-six sub-topics, and "1 of 4 topics" would hide that. */}
            <div style={{ fontSize:11.5, color:'var(--text2)' }}>questions answered</div>
          </div>
        </div>
        {pct < 100 && (
          <div style={{ marginTop:10, fontSize:12.5, color:'#B26A00' }}>
            {d.total - d.answered} still need your answer.
          </div>
        )}
      </div>

      {msg && <div style={{ fontSize:13, marginBottom:10, color:'var(--green)' }}>{msg}</div>}

      <div style={{ display:'grid', gap:10 }}>
        {!d.topics.length && <div className="card"><div className="empty-state"><p>No topics configured yet.</p></div></div>}
        {d.topics.map(t => {
          const subs = t.subtopics || [];
          const done = subs.filter(s => s.state).length;
          const needs = subs.filter(s => s.state === 'needs_training').length;
          const open = expanded[t.topic_id] ?? (subs.length > 0 && done < subs.length);
          const edge = subs.length
            ? (done === 0 ? 'var(--border)' : needs ? '#B26A00' : 'var(--green)')
            : (t.state === 'known' ? 'var(--green)' : t.state === 'needs_training' ? '#B26A00' : 'var(--border)');
          return (
          <div key={t.topic_id} className="card" style={{ margin:0, borderLeft:`3px solid ${edge}` }}>
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:240 }}>
                <div style={{ fontWeight:600, fontSize:13.5 }}>{t.title}</div>
                {t.description && <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>{t.description}</div>}
                {subs.length > 0 && (
                  <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
                    {done} of {subs.length} answered
                    {needs > 0 && <span style={{ color:'#B26A00' }}> · {needs} need training</span>}
                  </div>
                )}
              </div>
              {/* A topic with sub-topics is answered line by line — one answer
                  for thirty-six lines cannot say which of them you need. */}
              {subs.length > 0 ? (
                <div className="flex-gap" style={{ gap:6, minWidth:230, justifyContent:'flex-end', flexWrap:'wrap' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => bulk(t.topic_id, 'known')}>
                    ✓ I know all of these
                  </button>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setExpanded(e => ({ ...e, [t.topic_id]: !open }))}>
                    {open ? 'Hide' : `Answer ${subs.length}`}
                  </button>
                </div>
              ) : (
                <div style={{ minWidth:230 }}>
                  <div className="flex-gap" style={{ gap:6 }}>
                    <button className={`btn btn-sm ${t.state === 'known' ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex:1 }} onClick={() => answer(t.topic_id, 'known')}>✓ I know this</button>
                    <button className={`btn btn-sm ${t.state === 'needs_training' ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex:1 }} onClick={() => answer(t.topic_id, 'needs_training')}>🙋 I need training</button>
                  </div>
                  <input style={{ marginTop:6, fontSize:12 }}
                    defaultValue={t.note || ''}
                    onChange={e => setNotes(n => ({ ...n, [t.topic_id]: e.target.value }))}
                    onBlur={() => t.state && answer(t.topic_id, t.state)}
                    placeholder="Anything specific? (optional)" />
                  {t.updated_at && <div style={{ fontSize:10.5, color:'var(--text2)', marginTop:3 }}>
                    Answered {fmtStamp(t.updated_at)}</div>}
                </div>
              )}
            </div>

            {subs.length > 0 && open && (
              <div style={{ marginTop:10, borderTop:'1px solid var(--border)' }}>
                {subs.map((s, i) => (
                  <div key={s.id} className="flex-gap"
                    style={{ justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap',
                      padding:'7px 0', borderBottom: i < subs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ flex:1, minWidth:220, fontSize:12.5,
                      color: s.state ? 'var(--text)' : 'var(--text2)' }}>
                      <span style={{ color:'var(--text2)', marginRight:6 }}>{i + 1}.</span>{s.title}
                    </div>
                    <div className="flex-gap" style={{ gap:5 }}>
                      <button className={`btn btn-sm ${s.state === 'known' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => answer(t.topic_id, 'known', s.id)}>✓ Know</button>
                      <button className={`btn btn-sm ${s.state === 'needs_training' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => answer(t.topic_id, 'needs_training', s.id)}>🙋 Need training</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Training Needs: what the team asked for, ranked ─────────────────────────
function TrainingNeeds({ onPlan }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get('/trainings/assessment/demand').then(r => setD(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const maxNeeds = Math.max(1, ...d.topics.map(t => t.needs));
  const outstanding = d.team.filter(u => u.pending > 0);

  return (
    <div>
      <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:12 }}>
        What the team asked for, most-requested first. Schedule a session straight from a row and the
        people who requested it are pre-selected.
      </div>

      {outstanding.length > 0 && (
        <div className="card" style={{ marginBottom:14, borderLeft:'3px solid #B26A00' }}>
          <b style={{ fontSize:13 }}>Assessment still outstanding</b>
          <div style={{ fontSize:12.5, color:'var(--text2)', marginTop:4 }}>
            {outstanding.map(u => `${u.full_name} (${u.answered}/${u.total})`).join(' · ')}
          </div>
          <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:4 }}>
            Numbers below only count people who have answered.
          </div>
        </div>
      )}

      {/* The sub-topic list is what a session is actually built from: "5 people
          want SSL inspection" is a plan, "5 people want Firewall" is not. */}
      {(d.subtopics || []).length > 0 && (
        <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
          <b style={{ fontSize:13.5, display:'block', padding:'10px 12px' }}>
            Most-wanted sub-topics
            <span style={{ fontWeight:400, fontSize:11.5, color:'var(--text2)', marginLeft:8 }}>
              what to actually put in a session
            </span>
          </b>
          <div className="table-wrap"><table>
            <thead><tr>
              <th style={{ width:34 }}>#</th><th>Sub-topic</th><th>Topic</th>
              <th style={{ textAlign:'center' }}>Need Training</th>
              <th>Requested By</th>
            </tr></thead>
            <tbody>
              {d.subtopics.slice(0, 25).map((s, i) => (
                <tr key={s.id}>
                  <td style={{ color:'var(--text2)' }}>{i + 1}</td>
                  <td style={{ fontWeight:600 }}>{s.title}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{s.topic_title}</td>
                  <td style={{ textAlign:'center', fontSize:15, fontWeight:700, color:'#B26A00' }}>{s.needs}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>
                    {(s.requested_by || []).map(u => u.name).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {d.subtopics.length > 25 && (
            <div style={{ padding:'8px 12px', fontSize:11.5, color:'var(--text2)' }}>
              … and {d.subtopics.length - 25} more with requests.
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap"><table>
          <thead><tr>
            <th style={{ width:34 }}>#</th><th>Training Topic</th>
            <th style={{ textAlign:'center' }}>Need Training</th>
            <th style={{ textAlign:'center' }}>Already Know</th>
            <th style={{ minWidth:120 }}>Demand</th>
            <th>Requested By</th><th>Last Scheduled</th><th>Action</th>
          </tr></thead>
          <tbody>
            {!d.topics.length && <tr><td colSpan={8} style={{ padding:16, fontSize:13, color:'var(--text2)' }}>No topics configured yet.</td></tr>}
            {d.topics.map((t, i) => (
              <tr key={t.id}>
                <td style={{ color:'var(--text2)' }}>{i + 1}</td>
                <td style={{ fontWeight:600 }}>{t.title}</td>
                <td style={{ textAlign:'center', fontSize:15, fontWeight:700,
                  color: t.needs > 0 ? '#B26A00' : 'var(--text2)' }}>{t.needs}</td>
                <td style={{ textAlign:'center', fontSize:13, color:'var(--green)' }}>{t.known}</td>
                <td>
                  <div style={{ height:7, borderRadius:4, background:'var(--bg3)', overflow:'hidden' }}>
                    <div style={{ width:`${Math.round(t.needs / maxNeeds * 100)}%`, height:'100%', background:'#F5A524' }} />
                  </div>
                </td>
                <td style={{ fontSize:12, color:'var(--text2)', maxWidth:240 }}>
                  {(t.requested_by || []).length
                    ? t.requested_by.map(r => r.name + (r.note ? ` (${r.note})` : '')).join(', ')
                    : '—'}
                </td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{t.last_scheduled ? fmtDate(t.last_scheduled) : 'Never'}</td>
                <td>
                  <button className="btn btn-primary btn-sm" disabled={!t.needs}
                    onClick={() => onPlan(t)}>Plan Training</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// ── Topics & Lessons: the catalogue you schedule from ───────────────────────
function TopicsTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);      // null | {} new | topic
  const [csv, setCsv] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/trainings/topics?all=true').then(r => setRows(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const remove = async t => {
    const warn = Number(t.used)
      ? `"${t.title}" has been used in ${t.used} session(s), so it will be retired rather than deleted. Continue?`
      : `Remove "${t.title}"?`;
    if (!window.confirm(warn)) return;
    const r = await api.delete(`/trainings/topics/${t.id}`).catch(()=>null);
    flash(r?.data?.retired ? '✓ Topic retired (past sessions keep it)' : '✓ Topic removed');
    load();
  };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', marginBottom:12, gap:10, flexWrap:'wrap' }}>
        <div style={{ fontSize:12.5, color:'var(--text2)', maxWidth:520 }}>
          Configure your topics and lessons here once. When you schedule a session you pick a topic and
          its lesson comes with it — and editing a topic later never changes what a past session taught.
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setCsv(true)}>⬆ Bulk Upload</button>
          <button className="btn btn-primary btn-sm" onClick={() => setEdit({})}>+ Add Topic</button>
        </div>
      </div>

      {msg && <div style={{ fontSize:13, marginBottom:10, color:'var(--green)' }}>{msg}</div>}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="table-wrap"><table>
          <thead><tr>
            <th style={{ width:34 }}>#</th><th>Main Topic</th><th>Sub Topics</th>
            <th style={{ textAlign:'center' }}>Count</th><th style={{ textAlign:'center' }}>Used</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={7} style={{ padding:16, fontSize:13, color:'var(--text2)' }}>
              No topics yet. Add one, or bulk upload your list.</td></tr>}
            {rows.map((t, i) => (
              <tr key={t.id} style={{ opacity: t.active ? 1 : 0.55 }}>
                <td style={{ color:'var(--text2)' }}>{i + 1}</td>
                <td style={{ fontWeight:600 }}>{t.title}</td>
                <td style={{ fontSize:12, color:'var(--text2)', maxWidth:340 }}>
                  {summarise((t.subtopics || []).map(s => s.title).join('\n') || t.lesson, 90)}</td>
                <td style={{ textAlign:'center', fontSize:12 }}>{(t.subtopics || []).length || bullets(t.lesson).length}</td>
                <td style={{ textAlign:'center', fontSize:12 }}>{t.used}</td>
                <td>{t.active
                  ? <span className="badge" style={{ fontSize:10.5, background:'rgba(45,168,102,0.15)', color:'var(--green)' }}>Active</span>
                  : <span className="badge" style={{ fontSize:10.5, background:'var(--bg3)', color:'var(--text2)' }}>Retired</span>}</td>
                <td>
                  <div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEdit(t)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => remove(t)}>Remove</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {edit && <TopicModal topic={edit} onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); flash('✓ Topic saved'); load(); }} />}
      {csv && <TopicUpload onClose={() => setCsv(false)} onDone={() => { load(); }} />}
    </div>
  );
}

// Accepts either shape: one row per topic with a multi-line lesson cell, or one
// row per lesson point repeating the topic. The API merges repeated titles.
function TopicUpload({ onClose, onDone }) {
  return (
    <CSVUploadModal
      title="Bulk Upload Training Topics"
      columns={[{ key:'title', label:'Training Topic' }, { key:'lesson', label:'Lesson' }]}
      // Shows both accepted shapes: a whole lesson in one quoted cell, and the
      // same topic repeated with one point per row.
      sampleFile={{
        name: 'training_topics_sample.csv',
        content: 'Training Topic,Lesson\r\n'
          + 'Firewall Basics,"Understand security zones\nCreate a NAT policy\nRead the traffic log"\r\n'
          + 'Printer Troubleshooting,Clear a paper jam\r\n'
          + 'Printer Troubleshooting,Replace the toner cartridge\r\n'
          + 'Backup and Restore,"Check the nightly backup ran\nRestore a single file\nVerify the restored data"\r\n',
      }}
      formatHint={<>
        <strong>Required:</strong> Training Topic. <strong>Optional:</strong> Lesson.<br />
        Put the whole lesson in one cell (one point per line), <em>or</em> repeat the topic
        on several rows with one point each — repeated topics are merged.
        Re-uploading a sheet updates the existing topic instead of duplicating it.
      </>}
      mapRow={raw => {
        const get = (...keys) => {
          for (const k of keys) {
            const hit = Object.keys(raw).find(h => h.trim().toLowerCase() === k);
            if (hit && String(raw[hit]).trim()) return String(raw[hit]).trim();
          }
          return '';
        };
        const title = get('training topic','topic','title','training','subject');
        if (!title) return null;
        return { title, lesson: get('lesson','lesson taught','content','details','description','points') };
      }}
      onUpload={rows => api.post('/trainings/topics/import', { records: rows }).then(r => r.data)}
      onDone={onDone}
      onClose={onClose}
    />
  );
}

function TopicModal({ topic, onClose, onSaved }) {
  const isNew = !topic.id;
  const [f, setF] = useState({ title: topic.title || '', description: topic.description || '', active: topic.active !== false });
  // Sub-topics are edited as a list — one line each — and become the checkboxes
  // the trainer and trainee tick.
  const [subs, setSubs] = useState(
    (topic.subtopics || []).map(s => s.title).join('\n') || topic.lesson || '');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const lines = bullets(subs);

  const save = async () => {
    if (!f.title.trim()) { setErr('Main topic is required'); return; }
    if (!lines.length)   { setErr('Add at least one sub topic'); return; }
    setBusy(true); setErr('');
    const body = { ...f, subtopics: lines };
    try {
      if (isNew) await api.post('/trainings/topics', body);
      else await api.put(`/trainings/topics/${topic.id}`, body);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header"><span className="modal-title">{isNew ? 'Add Training Topic' : 'Edit Topic'}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group"><label>Main topic *</label>
          <input value={f.title} onChange={e => setF(s => ({ ...s, title:e.target.value }))}
            placeholder="e.g. Firewall Configuration & Troubleshooting" /></div>
        <div className="form-group"><label>Description <span style={{ fontSize:11, color:'var(--text2)' }}>— one line shown above the lesson</span></label>
          <input value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))}
            placeholder="e.g. Overview and hands-on training on firewall configuration and best practices." /></div>
        <div className="form-group"><label>Sub topics * <span style={{ fontSize:11, color:'var(--text2)' }}>— one per line; each becomes a tick box</span></label>
          <textarea rows={8} value={subs} onChange={e => setSubs(e.target.value)}
            placeholder={'Firewall interface overview\nWAN/LAN configuration\nPort configuration\nTroubleshooting disconnected interfaces'} />
          {lines.length > 0 && (
            <div style={{ marginTop:8, padding:'8px 10px', background:'var(--bg3)', borderRadius:'var(--radius)' }}>
              <div style={{ fontSize:11, color:'var(--text2)', marginBottom:5 }}>{lines.length} sub topic(s) — this is what gets ticked:</div>
              {lines.map((l, i) => (
                <div key={i} style={{ fontSize:12.5, display:'flex', gap:7, alignItems:'center', padding:'1px 0' }}>
                  <input type="checkbox" disabled readOnly /> {l}
                </div>
              ))}
            </div>
          )}
        </div>
        {!isNew && (
          <label className="opt" style={{ marginBottom:10 }}>
            <input type="checkbox" checked={f.active} onChange={e => setF(s => ({ ...s, active:e.target.checked }))} />
            <span>Active — offered when scheduling</span>
          </label>
        )}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── IT person: My Training ──────────────────────────────────────────────────
function MyTraining({ openTraining, setOpenTraining }) {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');          // all | not_started | in_progress | completed
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/trainings/my').then(r => setRows(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);

  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const count = s => rows.filter(r => r.my_status === s).length;
  const shown = rows
    .filter(r => tab === 'all' || r.my_status === tab)
    .filter(r => !q.trim() || `${r.topic} ${r.conducted_by || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const sel = rows.find(r => r.id === openTraining) || null;

  // Setting the status straight from the table, as the design shows.
  const setStatus = async (t, status) => {
    try { await api.post(`/trainings/${t.id}/status`, { status }); flash('✓ Training status updated'); load(); }
    catch (e) { flash(e.response?.data?.error || 'Could not update'); }
  };

  // Opening a lesson records that it was read — it never changes the status.
  useEffect(() => {
    if (sel && !sel.lesson_read_at) api.post(`/trainings/${sel.id}/read`).then(load).catch(()=>{});
  }, [sel, load]);

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:12, flexWrap:'wrap' }}>
        <div style={{ fontSize:12.5, color:'var(--text2)' }}>
          View training topics, read the lesson details and <b style={{ color:'var(--text)' }}>update your status</b>.
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍  Search training topics…"
          style={{ maxWidth:300 }} />
      </div>

      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:12, flexWrap:'wrap' }}>
        {[['all',`All Training (${rows.length})`],
          ['not_started',`Not Started (${count('not_started')})`],
          ['in_progress',`In Progress (${count('in_progress')})`],
          ['completed',`Completed (${count('completed')})`]].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'7px 16px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
              borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === id ? 'var(--accent)' : 'var(--text2)',
              fontWeight: tab === id ? 600 : 400, marginBottom:-1 }}>{label}</button>
        ))}
      </div>

      {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}

      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:14 }}>
        <div className="table-wrap"><table>
          <thead><tr>
            <th style={{ width:36 }}>#</th><th>Training Topic</th>
            <th>Training Date</th><th>Conducted By</th><th>Current Status</th><th>Last Updated On</th><th>Action</th>
          </tr></thead>
          <tbody>
            {!shown.length && <tr><td colSpan={7} style={{ padding:16, fontSize:13, color:'var(--text2)' }}>
              {q.trim() ? 'No training matches your search.' : 'No training here yet.'}</td></tr>}
            {shown.map((r, i) => (
              <tr key={r.id} style={{ background: r.id === openTraining ? 'var(--bg3)' : undefined }}>
                <td style={{ color:'var(--text2)' }}>{i + 1}</td>
                <td style={{ fontWeight:600 }}>
                  {r.topic}
                  {r.kind === 'new_staff' && <span className="badge" style={{ marginLeft:6, fontSize:9, background:'rgba(79,142,247,0.15)', color:'var(--accent)' }}>NEW STAFF</span>}
                </td>
                <td style={{ fontSize:12.5 }}>{fmtDate(r.training_date)}</td>
                <td style={{ fontSize:12.5 }}>{r.conducted_by || '—'}</td>
                <td>
                  {/* Status is settable straight from the list, as designed */}
                  <select value={r.my_status} onChange={e => setStatus(r, e.target.value)}
                    style={{ width:130, fontSize:12, fontWeight:600, padding:'4px 6px',
                      color:STATUS[r.my_status].colour, background:STATUS[r.my_status].bg,
                      border:`1px solid ${STATUS[r.my_status].colour}44`, borderRadius:6 }}>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  {r.my_needs_more && <div style={{ fontSize:10, color:'var(--red)', marginTop:2 }}>needs more</div>}
                </td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>
                  {r.my_status === 'not_started' && !r.lesson_read_at ? '–' : fmtStamp(r.my_updated_at)}
                </td>
                <td>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => setOpenTraining(r.id === openTraining ? null : r.id)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {sel && <LessonPanel t={sel} onDone={() => { load(); }} flash={flash} />}
    </div>
  );
}

function LessonPanel({ t, onDone, flash }) {
  const [status, setStatus] = useState(t.my_status);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [d, setD] = useState(null);            // detail: sub-topics + status history
  const [ticked, setTicked] = useState([]);
  useEffect(() => { setStatus(t.my_status); }, [t.id, t.my_status]);
  useEffect(() => {
    api.get(`/trainings/${t.id}`).then(r => {
      setD(r.data);
      setTicked(r.data.items.filter(i => i.my_checked).map(i => i.id));
    }).catch(()=>{});
  }, [t.id, t.my_updated_at]);

  const items = d?.items || [];
  const history = d?.history || [];
  const toggle = id => setTicked(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);

  // Ticks and status are submitted together — one action for the trainee.
  const save = async () => {
    setBusy(true);
    try {
      if (items.length) await api.post(`/trainings/${t.id}/my-items`, { checked_ids: ticked });
      await api.post(`/trainings/${t.id}/status`, { status });
      flash('✓ Training status updated');
      onDone();
    }
    catch (e) { flash(e.response?.data?.error || 'Could not update'); }
    finally { setBusy(false); }
  };
  const askMore = async () => {
    setBusy(true);
    try { await api.post(`/trainings/${t.id}/need-more`, { note }); flash('✓ Request sent to your trainer'); setAsking(false); setNote(''); onDone(); }
    catch (e) { flash(e.response?.data?.error || 'Could not send'); }
    finally { setBusy(false); }
  };

  const lines = bullets(t.lesson);
  const Panel = ({ title, children }) => (
    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', marginBottom:12 }}>
      <div style={{ fontSize:12.5, fontWeight:600, color:'var(--accent)', padding:'8px 12px',
        background:'rgba(79,142,247,0.06)', borderBottom:'1px solid var(--border)' }}>{title}</div>
      <div style={{ padding:'10px 12px' }}>{children}</div>
    </div>
  );

  return (
    <div className="card">
      <div className="flex-gap" style={{ gap:10, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
        <span style={{ fontSize:17, fontWeight:700 }}>{t.topic}</span>
        <span className="badge" style={{ fontSize:11, fontWeight:600,
          background:STATUS[t.my_status].bg, color:STATUS[t.my_status].colour }}>{STATUS[t.my_status].label}</span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'minmax(300px, 1.6fr) minmax(260px, 1fr)', gap:16, alignItems:'start' }}>
        <div>
          <Panel title="Training Information">
            {[['Conducted By', t.conducted_by || '—'],
              ['Training Date', fmtDate(t.training_date)],
              ['Conducted On', t.conducted_on ? fmtStamp(t.conducted_on) : 'Not yet conducted'],
              ['Description', t.description || '—']].map(([k, v]) => (
              <div key={k} style={{ display:'flex', fontSize:12.5, padding:'3px 0' }}>
                <span style={{ width:110, color:'var(--text2)', flexShrink:0 }}>{k}</span>
                <span style={{ color:'var(--text2)', flexShrink:0 }}>:&nbsp;</span>
                <span style={{ flex:1 }}>{v}</span>
              </div>
            ))}
          </Panel>

          <Panel title="Lesson Details">
            <div style={{ fontSize:11, color:'var(--text2)', marginBottom:6 }}>Tick what you have learned.</div>
          {items.length ? (
            <>
              {items.map(i => (
                <label key={i.id} className="opt opt-top" style={{ padding:'4px 0' }}>
                  <input type="checkbox" checked={ticked.includes(i.id)} onChange={() => toggle(i.id)} />
                  <span>
                    {i.title}
                    {i.covered && <span title="Covered by the trainer" style={{ marginLeft:6, fontSize:10.5, color:'var(--green)' }}>✓ taught</span>}
                  </span>
                </label>
              ))}
              <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:6 }}>
                {ticked.length} of {items.length} ticked
                {ticked.length < items.length && ' — if something is missing, ask for additional training.'}
              </div>
            </>
          ) : lines.length
            ? <ol style={{ margin:0, paddingLeft:20, fontSize:13, lineHeight:1.8 }}>{lines.map((l, i) => <li key={i}>{l}</li>)}</ol>
            : <div style={{ fontSize:13, color:'var(--text2)' }}>No sub topics were added.</div>}
          </Panel>
        </div>

        <div>
          <Panel title="Update Your Status">
            {Object.entries(STATUS).map(([k, v]) => (
              <label key={k} className="opt opt-top" style={{ padding:'6px 0' }}>
                <input type="radio" name={`st-${t.id}`} checked={status === k} onChange={() => setStatus(k)} />
                <span>
                  <div style={{ fontWeight:600 }}>{v.label}</div>
                  <div style={{ fontSize:11.5, color:'var(--text2)' }}>{v.hint}</div>
                </span>
              </label>
            ))}
            <button className="btn btn-primary" style={{ width:'100%', marginTop:8 }}
              disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Update Status'}
            </button>
            <button className="btn btn-secondary btn-sm" style={{ width:'100%', marginTop:6 }}
              onClick={() => setAsking(a => !a)}>🙋 Need additional training</button>
            {asking && (
              <div style={{ marginTop:6 }}>
                <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="What should be covered again? (optional)" />
                <button className="btn btn-secondary btn-sm" style={{ width:'100%', marginTop:4 }}
                  disabled={busy} onClick={askMore}>Send request</button>
              </div>
            )}
          </Panel>

          <Panel title="Your Status History">
            {!history.length
              ? <div style={{ fontSize:12.5, color:'var(--text2)' }}>No status set yet.</div>
              : (
                <table style={{ width:'100%' }}>
                  <thead><tr>
                    <th style={{ width:28, fontSize:11 }}>#</th><th style={{ fontSize:11 }}>Status</th><th style={{ fontSize:11 }}>Updated On</th>
                  </tr></thead>
                  <tbody>{history.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{i + 1}</td>
                      <td><span className="badge" style={{ fontSize:10.5, fontWeight:600,
                        background:STATUS[h.status].bg, color:STATUS[h.status].colour }}>{STATUS[h.status].label}</span></td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{fmtStamp(h.changed_at)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

// ── IT Lead: team dashboard ─────────────────────────────────────────────────
function TeamDashboard({ onOpen, planFrom, clearPlan }) {
  const [d, setD] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/trainings/dashboard').then(r => setD(r.data)).catch(()=>{}), []);
  useEffect(() => { load(); }, [load]);
  // Arriving from "Plan Training" on the needs table opens the scheduler.
  useEffect(() => { if (planFrom) setCreating(true); }, [planFrom]);
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const downloadReport = async () => {
    let rows = [];
    try { rows = (await api.get('/trainings/report')).data; }
    catch { flash('Could not build the report'); return; }
    if (!rows.length) { flash('No training to report on yet'); return; }
    const kindLabel = k => k === 'new_staff' ? 'New Staff Training' : 'IT Training';
    const detailRows = rows.map(r => ({
      'Type': kindLabel(r.kind), 'Topic': r.topic, 'Content': r.content || '',
      'Trainer': r.trainer || '', 'Training Date': r.training_date || '',
      'Conducted On': fmtStamp(r.conducted_on), 'Trainee': r.trainee || '',
      'Campus': r.campus_name || '',
      'Status': STATUS[r.status]?.label || '—',
      'Lesson Read': fmtStamp(r.lesson_read_at), 'Completed On': fmtStamp(r.completed_at),
      'Last Updated': fmtStamp(r.updated_at),
      'Needs More Training': r.needs_more ? 'Yes' : '', 'Their Note': r.note || '',
    }));
    const per = {};
    rows.filter(r => r.trainee).forEach(r => {
      const t = per[r.training_id] || (per[r.training_id] = {
        'Type': kindLabel(r.kind), 'Topic': r.topic, 'Content': r.content || '',
        'Trainer': r.trainer || '', 'Training Date': r.training_date || '',
        'Conducted On': fmtStamp(r.conducted_on), all:[], done:[], prog:[], not:[] });
      t.all.push(r.trainee);
      if (r.status === 'completed') t.done.push(r.trainee);
      else if (r.status === 'in_progress') t.prog.push(r.trainee);
      else t.not.push(r.trainee);
    });
    const list = a => a.slice().sort().join(', ');
    const summary = Object.values(per).map(t => ({
      'Type': t.Type, 'Topic': t.Topic, 'Content': t.Content, 'Trainer': t.Trainer,
      'Training Date': t['Training Date'], 'Conducted On': t['Conducted On'],
      'Members': t.all.length, 'Member Names': list(t.all),
      'Completed': t.done.length, 'Completed By': list(t.done),
      'In Progress': t.prog.length, 'In Progress With': list(t.prog),
      'Not Started': t.not.length, 'Not Started By': list(t.not),
      'Completion %': t.all.length ? Math.round((t.done.length / t.all.length) * 100) : 0,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Detail');
    XLSX.writeFile(wb, `IT_Training_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (!d) return null;
  const Kpi = ({ icon, label, value, colour, sub }) => (
    <div className="card" style={{ margin:0, display:'flex', alignItems:'center', gap:12 }}>
      <div style={{ width:40, height:40, borderRadius:10, display:'flex', alignItems:'center',
        justifyContent:'center', fontSize:19, background:`${colour}1F`, flexShrink:0 }}>{icon}</div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:11.5, color:'var(--text2)' }}>{label}</div>
        <div style={{ fontSize:24, fontWeight:700, color:colour, lineHeight:1.15 }}>{value}</div>
        {sub && <div style={{ fontSize:11, color: colour, opacity:0.85, overflow:'hidden', textOverflow:'ellipsis' }}>{sub}</div>}
      </div>
    </div>
  );

  // Progress bar used in the per-topic table.
  const Bar = ({ pct }) => (
    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
      <div style={{ flex:1, minWidth:56, height:7, borderRadius:4, background:'var(--bg3)', overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%',
          background: pct === 100 ? 'var(--green)' : pct > 0 ? '#F5A524' : 'transparent' }} />
      </div>
      <span style={{ fontSize:11.5, color:'var(--text2)', width:32, textAlign:'right' }}>{pct}%</span>
    </div>
  );

  // Donut for overall team completion.
  const Donut = ({ pct }) => {
    const r = 52, c = 2 * Math.PI * r;
    return (
      <svg viewBox="0 0 140 140" style={{ width:150, height:150 }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--bg3)" strokeWidth="14" />
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--green)" strokeWidth="14"
          strokeDasharray={`${c * pct / 100} ${c}`} strokeLinecap="round"
          transform="rotate(-90 70 70)" />
        <text x="70" y="78" textAnchor="middle" style={{ fontSize:26, fontWeight:700, fill:'var(--text)' }}>{pct}%</text>
      </svg>
    );
  };

  return (
    <div>
      {msg && <div style={{ fontSize:13, marginBottom:10, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12, marginBottom:16 }}>
        <Kpi icon="📄" label="Total Trainings" value={d.kpi.total_trainings} colour="#7C5CFF" />
        <Kpi icon="✅" label="Completed"   value={d.kpi.completed}   colour="var(--green)" sub={`${d.kpi.completed_pct}%`} />
        <Kpi icon="🕓" label="In Progress" value={d.kpi.in_progress} colour="#B26A00"      sub={`${d.kpi.in_progress_pct}%`} />
        <Kpi icon="❗" label="Not Started" value={d.kpi.not_started} colour="var(--red)"   sub={`${d.kpi.not_started_pct}%`} />
        <Kpi icon="👥" label="Team Members" value={d.kpi.team_size}  colour="var(--accent)"
          sub={`(${(d.kpi.team_names || []).join(', ')})`} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 2.4fr) minmax(240px, 1fr)', gap:16, alignItems:'start' }}>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', padding:'10px 12px' }}>
            <b style={{ fontSize:13.5 }}>Training Progress by Topic</b>
            <div className="flex-gap" style={{ gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={downloadReport}>⬇ Training Report</button>
              <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>+ Schedule Training</button>
            </div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr>
              <th style={{ width:34 }}>#</th><th>Training Topic</th><th>Training Date</th><th>Conducted On</th>
              <th style={{ textAlign:'center' }}>Completed</th>
              <th style={{ textAlign:'center' }}>In Progress</th><th style={{ textAlign:'center' }}>Not Started</th>
              <th style={{ minWidth:110 }}>Progress</th><th>Action</th>
            </tr></thead>
            <tbody>
              {!d.trainings.length && <tr><td colSpan={9} style={{ padding:16, fontSize:13, color:'var(--text2)' }}>No training scheduled yet.</td></tr>}
              {d.trainings.map((t, i) => (
                <tr key={t.id}>
                  <td style={{ color:'var(--text2)' }}>{i + 1}</td>
                  <td style={{ fontWeight:600 }}>
                    {t.topic}
                    {t.kind === 'new_staff' && <span className="badge" style={{ marginLeft:6, fontSize:9, background:'rgba(79,142,247,0.15)', color:'var(--accent)' }}>NEW STAFF</span>}
                    <div style={{ fontSize:11, color:'var(--text2)' }}>by {t.conducted_by || '—'}</div>
                  </td>
                  <td style={{ fontSize:12.5 }}>{fmtDate(t.training_date)}</td>
                  <td style={{ fontSize:12 }}>{t.conducted_on ? fmtDate(String(t.conducted_on).slice(0,10)) : '–'}</td>
                  <td style={{ textAlign:'center', color:'var(--green)', fontWeight:600 }}>{t.completed}</td>
                  <td style={{ textAlign:'center', color:'#B26A00', fontWeight:600 }}>{t.in_progress}</td>
                  <td style={{ textAlign:'center', color:'var(--red)', fontWeight:600 }}>{t.not_started}</td>
                  <td><Bar pct={Number(t.members) ? Math.round(Number(t.completed) / Number(t.members) * 100) : 0} /></td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => setDetail(t.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div style={{ padding:'8px 12px', fontSize:11.5, color:'var(--text2)', borderTop:'1px solid var(--border)' }}>
            Click View Details to see individual team member status and timestamps.
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom:14, textAlign:'center' }}>
            <b style={{ fontSize:13.5, display:'block', marginBottom:6, textAlign:'left' }}>Overall Team Completion</b>
            <Donut pct={d.kpi.percent} />
            <div style={{ fontSize:12.5, color:'var(--text2)' }}>
              <b style={{ color:'var(--text)' }}>{d.kpi.completed_trainings} of {d.kpi.total_trainings}</b> trainings<br />completed by team
            </div>
          </div>

          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <b style={{ fontSize:13.5, display:'block', padding:'10px 12px' }}>Team Member Completion</b>
            <div className="table-wrap"><table>
              <thead><tr><th style={{ width:28 }}>#</th><th>Team Member</th>
                <th style={{ textAlign:'center' }}>Done</th><th style={{ textAlign:'center' }}>In Prog.</th>
                <th style={{ textAlign:'center' }}>Not St.</th><th style={{ textAlign:'center' }}>Completion</th></tr></thead>
              <tbody>{d.people.map((p, i) => (
                <tr key={p.id}>
                  <td style={{ color:'var(--text2)', fontSize:12 }}>{i + 1}</td>
                  <td style={{ fontWeight:500, fontSize:12.5 }}>{p.full_name}</td>
                  <td style={{ textAlign:'center', fontSize:12, color:'var(--green)' }}>{p.completed}</td>
                  <td style={{ textAlign:'center', fontSize:12, color:'#B26A00' }}>{p.in_progress}</td>
                  <td style={{ textAlign:'center', fontSize:12, color:'var(--red)' }}>{p.not_started}</td>
                  <td style={{ textAlign:'center' }}>
                    <span className="badge" style={{ fontSize:11, fontWeight:700,
                      background: p.percent === 100 ? 'rgba(45,168,102,0.15)' : p.percent >= 50 ? 'rgba(255,153,0,0.15)' : 'rgba(229,72,77,0.15)',
                      color: p.percent === 100 ? 'var(--green)' : p.percent >= 50 ? '#B26A00' : 'var(--red)' }}>
                      {p.percent}%
                    </span>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          </div>
        </div>
      </div>

      {/* Bottom row: activity trail and who is still outstanding */}
      <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1.4fr) minmax(280px, 1fr)', gap:16, marginTop:16, alignItems:'start' }}>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <b style={{ fontSize:13.5, display:'block', padding:'10px 12px' }}>Recent Training Activity</b>
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width:28 }}>#</th><th>Date &amp; Time</th><th>Team Member</th><th>Training Topic</th><th>Status</th></tr></thead>
            <tbody>
              {!d.activity.length && <tr><td colSpan={5} style={{ padding:14, fontSize:12.5, color:'var(--text2)' }}>Nothing yet.</td></tr>}
              {d.activity.map((a, i) => (
                <tr key={i}>
                  <td style={{ color:'var(--text2)', fontSize:12 }}>{i + 1}</td>
                  <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmtStamp(a.changed_at)}</td>
                  <td style={{ fontSize:12.5, fontWeight:500 }}>{a.full_name}</td>
                  <td style={{ fontSize:12.5 }}>
                    <button onClick={() => onOpen(a.training_id)}
                      style={{ background:'none', border:'none', padding:0, cursor:'pointer',
                        color:'var(--accent)', fontSize:12.5, textAlign:'left' }}>{a.topic}</button>
                  </td>
                  <td><span className="badge" style={{ fontSize:10.5, fontWeight:600,
                    background:STATUS[a.status].bg, color:STATUS[a.status].colour }}>{STATUS[a.status].label}</span></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>

        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <b style={{ fontSize:13.5, display:'block', padding:'10px 12px' }}>Upcoming / Pending Items</b>
          <div className="table-wrap"><table>
            <thead><tr><th style={{ width:28 }}>#</th><th>Training Topic</th><th>Pending Members</th><th>Action</th></tr></thead>
            <tbody>
              {!d.pending.length && <tr><td colSpan={4} style={{ padding:14, fontSize:12.5, color:'var(--green)' }}>Everyone is up to date. 🎉</td></tr>}
              {d.pending.map((p, i) => (
                <tr key={p.id}>
                  <td style={{ color:'var(--text2)', fontSize:12 }}>{i + 1}</td>
                  <td style={{ fontSize:12.5, fontWeight:500 }}>{p.topic}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{p.pending_members}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => setDetail(p.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>

      {creating && <ScheduleModal preset={planFrom}
        onClose={() => { setCreating(false); clearPlan?.(); }}
        onDone={n => { setCreating(false); clearPlan?.(); flash(`✓ Training scheduled for ${n} member(s)`); load(); }} />}
      {detail && <DetailModal id={detail} onClose={() => setDetail(null)} onChange={load} flash={flash} />}
    </div>
  );
}

function DetailModal({ id, onClose, onChange, flash }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ed, setEd] = useState({ topic:'', lesson:'', training_date:'' });
  const [covered, setCovered] = useState([]);
  const load = useCallback(() => api.get(`/trainings/${id}`).then(r => setD(r.data)).catch(()=>{}), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d) return;
    setEd({ topic: d.topic_pending ? '' : d.topic, lesson: d.lesson || '', training_date: d.training_date || '' });
    // Nothing ticked yet means the session hasn't been marked up — default to
    // all, since a trainer normally covers the whole list.
    const already = d.items.filter(i => i.covered).map(i => i.id);
    setCovered(already.length ? already : d.items.map(i => i.id));
  }, [d]);

  if (!d) return null;
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); flash(ok); load(); onChange?.(); }
    catch (e) { flash(e.response?.data?.error || 'Action failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:820 }}>
        <div className="modal-header"><span className="modal-title">{d.topic}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:12 }}>
          {d.kind === 'new_staff' && <span className="badge" style={{ fontSize:9.5, marginRight:6, background:'rgba(79,142,247,0.15)', color:'var(--accent)' }}>NEW STAFF</span>}
          {fmtDate(d.training_date)} · conducted by <b style={{ color:'var(--text)' }}>{d.conducted_by || '—'}</b>
          {d.conducted_on ? <span style={{ color:'var(--green)' }}> · Conducted {fmtStamp(d.conducted_on)}</span>
                          : <span style={{ color:'#B26A00' }}> · Not conducted yet</span>}
        </div>

        {d.can_edit && (d.topic_pending || editing) ? (
          <div className="card" style={{ marginBottom:12, borderLeft:'3px solid var(--accent)' }}>
            <div className="form-group"><label>Topic *</label>
              <input value={ed.topic} onChange={e => setEd(s => ({ ...s, topic:e.target.value }))} placeholder="What was covered?" /></div>
            <div className="form-group"><label>Lesson <span style={{ fontSize:11, color:'var(--text2)' }}>— one point per line; shown to the trainee as bullets</span></label>
              <textarea rows={6} value={ed.lesson} onChange={e => setEd(s => ({ ...s, lesson:e.target.value }))} /></div>
            <div className="form-group"><label>Training date</label>
              <input type="date" value={ed.training_date} onChange={e => setEd(s => ({ ...s, training_date:e.target.value }))} /></div>
            <div className="flex-gap" style={{ gap:8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy || !ed.topic.trim()}
                onClick={() => act(() => api.put(`/trainings/${id}`, ed), '✓ Saved').then(() => setEditing(false))}>Save</button>
              {!d.topic_pending && <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>}
            </div>
          </div>
        ) : (
          <div className="card" style={{ background:'var(--bg3)', marginBottom:12 }}>
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:11.5, fontWeight:700, color:'var(--text2)', letterSpacing:'.04em' }}>LESSON TAUGHT</div>
              <div className="flex-gap" style={{ gap:6 }}>
                {d.can_edit && <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button>}
                {d.can_edit && !d.conducted_on &&
                  <button className="btn btn-primary btn-sm" disabled={busy}
                    onClick={() => act(() => api.post(`/trainings/${id}/complete`), '✓ Marked as conducted')}>Mark conducted</button>}
              </div>
            </div>
            {/* The trainer ticks what was actually covered. Defaults to all,
                since a session normally works through the whole list. */}
            {d.items?.length ? (
              <>
                {d.items.map(i => (
                  <label key={i.id} className="opt" style={{ padding:'4px 0',
                    cursor: d.can_edit ? 'pointer' : 'default' }}>
                    <input type="checkbox" disabled={!d.can_edit}
                      checked={covered.includes(i.id)}
                      onChange={() => setCovered(a => a.includes(i.id) ? a.filter(x => x !== i.id) : [...a, i.id])} />
                    <span>{i.title}</span>
                    {i.covered_at && <span className="opt-meta">{fmtStamp(i.covered_at)}</span>}
                  </label>
                ))}
                {d.can_edit && (
                  <div className="flex-gap" style={{ gap:8, marginTop:8 }}>
                    <button className="btn btn-secondary btn-sm" disabled={busy}
                      onClick={() => act(() => api.post(`/trainings/${id}/items`, { covered_ids: covered }), '✓ Coverage saved')}>
                      Save coverage
                    </button>
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => setCovered(covered.length === d.items.length ? [] : d.items.map(i => i.id))}>
                      {covered.length === d.items.length ? 'Clear all' : 'Tick all'}
                    </button>
                    <span style={{ fontSize:11.5, color:'var(--text2)', alignSelf:'center' }}>
                      {covered.length} of {d.items.length} covered
                    </span>
                  </div>
                )}
              </>
            ) : bullets(d.lesson).length
              ? <ul style={{ margin:0, paddingLeft:18, fontSize:13, lineHeight:1.7 }}>{bullets(d.lesson).map((l, i) => <li key={i}>{l}</li>)}</ul>
              : <div style={{ fontSize:13, color:'var(--text2)' }}>No sub topics were added.</div>}
          </div>
        )}

        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ fontSize:11.5, fontWeight:700, color:'var(--text2)', padding:'10px 12px', letterSpacing:'.04em' }}>TEAM MEMBERS</div>
          <div className="table-wrap"><table>
            <thead><tr><th>Member</th><th>Campus</th><th>Status</th><th style={{ textAlign:'center' }}>Sub topics</th><th>Lesson Read</th><th>Completed On</th><th>Last Updated</th></tr></thead>
            <tbody>{d.members.map(m => (
              <tr key={m.id}>
                <td style={{ fontWeight:500 }}>{m.full_name}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{m.campus_name || '—'}</td>
                <td>
                  <span className="badge" style={{ fontSize:10.5, background:STATUS[m.status].bg, color:STATUS[m.status].colour }}>
                    {STATUS[m.status].label}</span>
                  {m.needs_more && <div style={{ fontSize:10.5, color:'var(--red)', marginTop:2 }} title={m.note || ''}>🙋 needs more</div>}
                </td>
                <td style={{ textAlign:'center', fontSize:12,
                  color: m.total_items && m.ticked < m.total_items ? '#B26A00' : 'var(--text)' }}>
                  {m.ticked}/{m.total_items}
                </td>
                <td style={{ fontSize:12 }}>{fmtStamp(m.lesson_read_at)}</td>
                <td style={{ fontSize:12 }}>{fmtStamp(m.completed_at)}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{fmtStamp(m.updated_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({ onClose, onDone, preset }) {
  const { user } = useAuth();
  const [f, setF] = useState({ kind:'it_training', topic_id:'', topic:'', lesson:'', description:'', training_date:'', trainer_user_id:'' });
  const [people, setPeople] = useState([]);
  const [topics, setTopics] = useState([]);
  const [custom, setCustom] = useState(false);       // topic not in the catalogue
  const [subIds, setSubIds] = useState([]);          // sub-topics this session covers
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const allSubs = (topics.find(t => String(t.id) === String(f.topic_id))?.subtopics) || [];

  useEffect(() => { api.get('/tasks/it-users').then(r => setPeople(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    api.get('/trainings/topics').then(r => {
      setTopics(r.data);
      // Planning from the needs table: select that topic and tick the people
      // who asked for it, so scheduling is one confirmation.
      if (preset) {
        const t = r.data.find(x => x.id === preset.id);
        if (t) {
          setF(s => ({ ...s, topic_id: String(t.id), topic: t.title,
            lesson: t.lesson || '', description: t.description || '' }));
          setSubIds((t.subtopics || []).map(s => s.id));
        }
        setPicked((preset.requested_by || []).map(r2 => r2.id));
      }
    }).catch(()=>{});
  }, [preset]);

  // Picking a catalogue topic brings its lesson with it; both stay editable for
  // this session without touching the catalogue.
  const pickTopic = id => {
    const t = topics.find(x => String(x.id) === String(id));
    setF(s => ({ ...s, topic_id: id, topic: t ? t.title : '', lesson: t ? (t.lesson || '') : s.lesson,
      description: t?.description || s.description }));
    setSubIds((t?.subtopics || []).map(s => s.id));    // all sub-topics by default
  };
  const isNewStaff = f.kind === 'new_staff';
  const trainerId = f.trainer_user_id ? parseInt(f.trainer_user_id) : user?.id;
  const trainees = people.filter(p => p.id !== trainerId);
  const all = trainees.length > 0 && trainees.every(p => picked.includes(p.id));
  const toggle = id => setPicked(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);

  const save = async () => {
    if (!isNewStaff && !f.topic.trim() && !f.topic_id) { setErr('Select a training topic'); return; }
    if (isNewStaff && !f.trainer_user_id) { setErr('Select the trainer'); return; }
    if (!picked.length) { setErr(isNewStaff ? 'Select the trainee' : 'Select at least one member'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.post('/trainings', { ...f, topic: f.topic.trim() || null,
        topic_id: f.topic_id || null, subtopic_ids: custom ? [] : subIds,
        trainer_user_id: f.trainer_user_id || null, trainee_ids: picked });
      onDone(r.data.trainees);
    } catch (e) { setErr(e.response?.data?.error || 'Could not schedule the training'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:620 }}>
        <div className="modal-header"><span className="modal-title">{isNewStaff ? 'Allocate New Staff Training' : 'Schedule IT Training'}</span>
          <button className="modal-close" onClick={onClose}>×</button></div>

        <div className="form-group"><label>Training type *</label>
          <div style={{ display:'grid', gap:8 }}>
            {[['it_training','IT Training','You set the topic and lesson, conduct the session, then the team update their own status.'],
              ['new_staff','New Staff Training','Allocate a trainer and a trainee. The trainer adds the topic; the trainee confirms they learned it.']].map(([v,t,desc]) => (
              <label key={v} className="card opt opt-top" style={{ padding:'10px 12px', margin:0,
                background: f.kind === v ? 'var(--bg3)' : undefined }}>
                <input type="radio" name="tkind" checked={f.kind === v}
                  onChange={() => { setF(s => ({ ...s, kind:v })); setPicked([]); }} />
                <span><b style={{ fontSize:13 }}>{t}</b><div style={{ fontSize:12, color:'var(--text2)' }}>{desc}</div></span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group"><label>Conducted by {isNewStaff ? '*' : <span style={{ fontSize:11, color:'var(--text2)' }}>— defaults to you</span>}</label>
          <select value={f.trainer_user_id} onChange={e => { setF(s => ({ ...s, trainer_user_id:e.target.value })); setPicked([]); }}>
            <option value="">{isNewStaff ? '— Select the trainer —' : `Me (${user?.full_name})`}</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.full_name}{p.campus_name ? ` · ${p.campus_name}` : ''}</option>)}
          </select></div>

        <div className="form-group">
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <label style={{ margin:0 }}>Training topic {isNewStaff
              ? <span style={{ fontSize:11, color:'var(--text2)' }}>— optional; the trainer fills this in</span> : '*'}</label>
            <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => { setCustom(c => !c); setF(s => ({ ...s, topic_id:'', topic:'' })); }}>
              {custom ? 'Pick from list' : 'Use a custom topic'}
            </button>
          </div>
          {custom ? (
            <input value={f.topic} onChange={e => setF(s => ({ ...s, topic:e.target.value }))}
              placeholder={isNewStaff ? 'Leave blank for the trainer to set' : 'One-off topic not in your list'} />
          ) : (
            <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', maxHeight:150, overflowY:'auto', padding:'4px 0' }}>
              {!topics.length && (
                <div style={{ padding:'8px 10px', fontSize:12, color:'#B26A00' }}>
                  No topics configured yet — add them under Topics &amp; Lessons, or use a custom topic.
                </div>
              )}
              {topics.map(t => (
                <label key={t.id} className="opt" style={{ padding:'6px 10px' }}>
                  <input type="radio" name="main-topic" checked={String(f.topic_id) === String(t.id)}
                    onChange={() => pickTopic(t.id)} />
                  <span>{t.title}</span>
                  <span className="opt-meta">{(t.subtopics || []).length} sub topics</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Sub-topics of the chosen main topic — untick anything not part of
            this session. These become the boxes both sides tick afterwards. */}
        {!custom && f.topic_id && (
          <div className="form-group">
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <label style={{ margin:0 }}>Sub topics covered <span style={{ fontSize:11, color:'var(--text2)' }}>— ticked by trainer and trainee after the session</span></label>
              <button type="button" className="btn btn-secondary btn-sm"
                onClick={() => setSubIds(allSubs.every(s => subIds.includes(s.id)) ? [] : allSubs.map(s => s.id))}>
                {allSubs.every(s => subIds.includes(s.id)) ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', maxHeight:150, overflowY:'auto', padding:'4px 0' }}>
              {allSubs.map(s => (
                <label key={s.id} className="opt" style={{ padding:'5px 10px' }}>
                  <input type="checkbox" checked={subIds.includes(s.id)}
                    onChange={() => setSubIds(a => a.includes(s.id) ? a.filter(x => x !== s.id) : [...a, s.id])} />
                  <span>{s.title}</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:5 }}>{subIds.length} of {allSubs.length} selected</div>
          </div>
        )}

        {custom && (
          <div className="form-group"><label>Sub topics <span style={{ fontSize:11, color:'var(--text2)' }}>— one per line; each becomes a tick box</span></label>
            <textarea rows={5} value={f.lesson} onChange={e => setF(s => ({ ...s, lesson:e.target.value }))}
              placeholder={'Firewall interface overview\nWAN/LAN configuration\nPort configuration'} /></div>
        )}

        <div className="form-group"><label>Description <span style={{ fontSize:11, color:'var(--text2)' }}>— one line shown above the lesson</span></label>
          <input value={f.description} onChange={e => setF(s => ({ ...s, description:e.target.value }))}
            placeholder="e.g. Overview and hands-on training on firewall configuration and best practices." /></div>

        <div className="form-group"><label>Training date</label>
          <input type="date" value={f.training_date} onChange={e => setF(s => ({ ...s, training_date:e.target.value }))} /></div>

        <div className="form-group">
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <label style={{ margin:0 }}>{isNewStaff ? 'Trainee *' : 'Team members *'}</label>
            {!isNewStaff && <button type="button" className="btn btn-secondary btn-sm"
              onClick={() => setPicked(all ? [] : trainees.map(p => p.id))}>{all ? 'Clear all' : 'All IT persons'}</button>}
          </div>
          <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', maxHeight:170, overflowY:'auto', padding:'4px 0' }}>
            {!trainees.length && <div style={{ padding:'8px 10px', fontSize:12.5, color:'var(--text2)' }}>No other IT staff found.</div>}
            {trainees.map(p => (
              <label key={p.id} className="opt" style={{ padding:'6px 10px' }}>
                <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                <span>{p.full_name}</span>
                {['it_head','it_lead'].includes(p.role) && <span className="badge" style={{ fontSize:9.5, background:'var(--bg3)', color:'var(--text2)' }}>
                  {p.role === 'it_head' ? 'IT HEAD' : 'IT LEAD'}</span>}
                {p.campus_name && <span className="opt-meta">{p.campus_name}</span>}
              </label>
            ))}
          </div>
          <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:5 }}>{picked.length} selected</div>
        </div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Scheduling…' : 'Schedule'}</button>
        </div>
      </div>
    </div>
  );
}
